import { Component, OnInit, ViewChild } from '@angular/core'
import { ActivatedRoute, ParamMap, Router } from '@angular/router'
import { lastValueFrom } from 'rxjs'
import { ActuatesService } from 'src/api/actuates-api/actuates-api.service'
import { MessageService } from 'src/app/components/message/message.service'
import { ConfirmService } from 'src/app/module/common-parts/confirm/confirm.service'
import { ActionButtonDisp, FooterButtonTextAlign } from 'src/app/module/common-parts/under-btn-disp/under-btn-disp.interface'
import { TargetServiceBlockInfo, ServiceMapViewBlock } from 'src/app/module/myidea-parts/service-map-view.interface'
import { ServiceMapViewService } from 'src/app/module/myidea-parts/service-map-view.service'
import { UraConactorLinkComponent } from '../ura-conector-link/ura-conector-link.component'

@Component({
  selector: 'app-ura-connect-service-map',
  templateUrl: './ura-connect-service-map.component.html'
})
export class UraConnectServiceMapComponent implements OnInit {
  title = 'サービスの接続変更'
  @ViewChild('connectSearch', { read: UraConactorLinkComponent, static: false })
  public connectSearch!: UraConactorLinkComponent
  organizationCompanyCd: string = ""
  contractOrganizationCd: string = ""
  serviceMap: ServiceMapViewBlock[] = []
  fromActuateCd: string = "" // コネクタ側
  toActuateCd: string = "" // Pack,ランチャー側
  targetProductCd: string = ""
  loading = true
  sending = false
  actionButtonsDisp: ActionButtonDisp[] = [
    {
      label: '戻る',
      textAlign: FooterButtonTextAlign.LEFT,
      actionFunction: () => {
        this.pageBack()
      }
    },
    {
      label: 'キャンセル',
      classes: ['c-btn-sub'],
      actionFunction: () => {
        this.selectCancel()
      }
    },
  ]
  actionButtonsDispForOtherCompany: ActionButtonDisp[] = [
    {
      label: '戻る',
      textAlign: FooterButtonTextAlign.LEFT,
      actionFunction: () => {
        this.pageBack()
      }
    },
    {
      label: 'キャンセル',
      classes: ['c-btn-sub'],
      actionFunction: () => {
        this.selectCancel()
      }
    },
    {
      label: '接続する',
      actionFunction: () => {
        const actuateCd = this.connectSearch.conectActuates()
        if (actuateCd) {
          if (this.fromActuateCd) {
            this.connect(this.fromActuateCd, actuateCd)
          }
          else if (this.toActuateCd) {
            this.connect(actuateCd, this.toActuateCd)
          }
        }
      }
    }
  ]
  selectTargetMode = false
  showTabKey = ''

  constructor(
    private serviceMapViewService: ServiceMapViewService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private actuatesService: ActuatesService,
    private messageService: MessageService,
    private confirmService: ConfirmService
  ) { }

  async ngOnInit(): Promise<void> {
    this.activatedRoute.paramMap.subscribe((params: ParamMap) => {
      this.organizationCompanyCd = params.get('organizationCompanyCd') || ''
      this.contractOrganizationCd = params.get('contractOrganizationCd') || ''
    })
    if (this.contractOrganizationCd) {
      await this.getServiceMap()
    }
  }

  async getServiceMap(): Promise<void> {
    this.loading = true
    // サービスマップ取得
    const apiMapData = await this.serviceMapViewService.getServiceMapSearchByOrgFromApi(this.contractOrganizationCd)
    const map = await this.serviceMapViewService.convertBaseServiceMap(apiMapData, this.organizationCompanyCd, this.contractOrganizationCd)
    this.serviceMap = await this.serviceMapViewService.addColumnForConnect(map, this.organizationCompanyCd)
    this.serviceMap = this.serviceMapViewService.addColumnCanEditMemoView(this.serviceMap)
    this.loading = false
  }

  // サービスブロッククリック時処理
  async clickServiceEvent(value: TargetServiceBlockInfo): Promise<void> {
    // 接続元選択
    if (!this.fromActuateCd && !this.toActuateCd) {
      if (value.targetBlock.classes?.find(x => x === 'connected-block')) {
        const pair = this.getDisconnectPairByRule(value.targetBlock)
        if (pair) {
          this.disconnect(pair.fromActuateCd, pair.toActuateCd)
        }
        return
      }
      if (value.targetBlock.classes?.find(x => x === 'unconnected-block')) {
        value.targetBlock.classes.push('back-blink-select')
        this.selectTargetMode = true
        const rule = this.serviceMapViewService.ActuateRelationRules
        let targetProductCds: string[] = []
        if (value.parentBlock) { // コネクタ側を選択した場合
          this.fromActuateCd = value.targetBlock.actuateCd!
          this.targetProductCd = value.targetBlock.productCd!
          targetProductCds = rule?.filter(x => x.fromProductCd === value?.targetBlock?.productCd!)?.map(x => x.toProductCd) || []
        }
        else { // Pack,ランチャー側を選択した場合
          this.toActuateCd = value.targetBlock.actuateCd!
          this.targetProductCd = value.targetBlock.productCd!
          targetProductCds = rule?.filter(x => x.toProductCd === value?.targetBlock?.productCd!)?.map(x => x.fromProductCd) || []
        }
        const selectedActuateCd = value.targetBlock.actuateCd || ''
        await this.highlightConnectCandidatesByApi(selectedActuateCd, targetProductCds)
      }
    }
    else {
      if (value.targetBlock.classes?.find(x => x === 'unconnected-block')) {
        if (this.fromActuateCd) {
          this.connect(this.fromActuateCd, value.targetBlock.actuateCd!)
        }
        else {
          this.connect(value.targetBlock.actuateCd!, this.toActuateCd)
        }
      }
    }
  }

  // 自社接続候補をAPIから取得し、候補ブロックのみ色付けする
  private async highlightConnectCandidatesByApi(selectedActuateCd: string, targetProductCds: string[]): Promise<void> {
    if (!selectedActuateCd) { return }
    const clearCandidateClass = (val: TargetServiceBlockInfo) => {
      val.targetBlock.classes = val.targetBlock.classes?.filter(
        x => x !== 'connected-block' && x !== 'unconnected-block'
      ) || []
    }
    this.serviceMapViewService.runForAllServiceFloor(this.serviceMap, clearCandidateClass)

    const uniqueTargetProductCds = Array.from(new Set(targetProductCds.filter(x => !!x)))
    const candidateKeySet = new Set<string>()

    try {
      for (const targetProductCd of uniqueTargetProductCds) {
        const ret$ = this.actuatesService.GetConnecterProducts(selectedActuateCd, targetProductCd, this.organizationCompanyCd)
        const ret = await lastValueFrom(ret$)
        if (ret?.resultCode !== 0) { continue }
        ret.data?.forEach(item => {
          candidateKeySet.add(`${item.actuateCd}::${item.productCd}`)
        })
      }

      const applyCandidateClass = (val: TargetServiceBlockInfo) => {
        const key = `${val.targetBlock.actuateCd}::${val.targetBlock.productCd}`
        if (candidateKeySet.has(key)) {
          val.targetBlock.classes?.push('unconnected-block')
        }
      }
      this.serviceMapViewService.runForAllServiceFloor(this.serviceMap, applyCandidateClass)
    }
    finally {
    }
  }

  // クリックした接続済みブロックから、relationRule を用いて切断ペア(from/to)を解決
  private getDisconnectPairByRule(targetBlock: ServiceMapViewBlock): { fromActuateCd: string, toActuateCd: string } | null {
    const clickedActuateCd = targetBlock.actuateCd
    const clickedActuateId = targetBlock.actuateId
    const clickedProductCd = targetBlock.productCd
    if (!clickedActuateCd || !clickedActuateId || !clickedProductCd) { return null }

    const rules = this.serviceMapViewService.ActuateRelationRules || []
    const fromRules = rules.filter(x => x.fromProductCd === clickedProductCd)
    const toRules = rules.filter(x => x.toProductCd === clickedProductCd)

    const flatBlocks: ServiceMapViewBlock[] = []
    this.serviceMapViewService.runForAllServiceFloor(this.serviceMap, (blockInfo: TargetServiceBlockInfo) => {
      flatBlocks.push(blockInfo.targetBlock)
    })

    // クリックブロックが from 側の場合: parentActuateIds から to 側を探す
    if (fromRules.length) {
      const toProductSet = new Set(fromRules.map(x => x.toProductCd))
      const toBlock = flatBlocks.find(block =>
        !!block.actuateCd
        && !!block.productCd
        && toProductSet.has(block.productCd)
        && !!block.parentActuateIds?.find(parentId => parentId === clickedActuateId)
      )
      const toActuateCd = toBlock?.actuateCd || ''
      if (toActuateCd) {
        return {
          fromActuateCd: clickedActuateCd,
          toActuateCd
        }
      }
    }

    // クリックブロックが to 側の場合: clicked の parentActuateIds から from 側を探す
    if (toRules.length) {
      const fromProductSet = new Set(toRules.map(x => x.fromProductCd))
      const clickedParentIdSet = new Set((targetBlock.parentActuateIds || []).filter(x => !!x))
      const fromBlock = flatBlocks.find(block =>
        !!block.actuateId
        && !!block.actuateCd
        && !!block.productCd
        && fromProductSet.has(block.productCd)
        && clickedParentIdSet.has(block.actuateId)
      )
      const fromActuateCd = fromBlock?.actuateCd || ''
      if (fromActuateCd) {
        return {
          fromActuateCd,
          toActuateCd: clickedActuateCd
        }
      }
    }

    return null
  }

  // キャンセルボタン押下時
  async selectCancel(): Promise<void> {
    this.selectTargetMode = false
    this.showTabKey = ''
    this.fromActuateCd = ''
    this.targetProductCd = ''
    this.toActuateCd = ''

    // 選択状態クラス(back-blink-select)削除
    const func = (value: TargetServiceBlockInfo) => {
      value.targetBlock.classes = value.targetBlock.classes?.filter(x => x !== 'back-blink-select')
    }
    this.serviceMapViewService.runForAllServiceFloor(this.serviceMap, func)

    this.serviceMap = await this.serviceMapViewService.addColumnForConnect(this.serviceMap, this.organizationCompanyCd)
  }

  // サービスマップ再取得
  async reloadMap(): Promise<void> {
    this.serviceMapViewService.clearKeepData(this.contractOrganizationCd)
    await new Promise(resolve => setTimeout(resolve, 1500)) // DB反映待ち
    await this.getServiceMap()
    await this.selectCancel()
  }

  pageBack(): void {
    this.router.navigate(['/contract-entry-list'])
  }

  // 切断
  async disconnect(fromActuateCd: string, toActuateCd: string): Promise<void> {
    if (fromActuateCd === toActuateCd) { return }
    const message = `切断しますか？`
    this.confirmService.confirm(message, '', async () => {
      if (this.sending) { return }
      this.sending = true
      const ret = await this.actuatesService.actuatesDisconnect(fromActuateCd, toActuateCd)
      this.sending = false
      if (ret?.resultCode !== 0) { return }
      this.messageService.success('サービスの接続を切断しました')
      await this.reloadMap()
    })
  }

  // 接続
  async connect(fromActuateCd: string, toActuateCd: string): Promise<void> {
    if (fromActuateCd === toActuateCd) { return }
    const message = `接続しますか？`
    this.confirmService.confirm(message, '', async () => {
      if (this.sending) { return }
      this.sending = true
      const ret = await this.actuatesService.actuatesConnect(fromActuateCd, toActuateCd)
      this.sending = false
      if (ret?.resultCode !== 0) { return }
      this.messageService.success('サービスを接続しました')
      await this.reloadMap()
    })
  }
}
